import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_TERMINAL_STATUSES = new Set([
  "ready",
  "completed",
  "failed",
  "cancelled",
  "failed_or_cancelled",
  "action_required",
]);

export default function useWorkLifecycle({
  enabled = true,
  isOpen = false,
  isActive = false,
  resourceId,
  contextPayload = {},
  adapter,
  buildRevisionContextFromResponse,
  onError,
}) {
  const [sourceResourceId, setSourceResourceId] = useState(resourceId || null);
  const [lifecycleRequestId, setLifecycleRequestId] = useState(null);
  const [mode, setMode] = useState("form");
  const [lifecycle, setLifecycle] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revisionSubmitting, setRevisionSubmitting] = useState(false);
  const [statusItemUpdateSubmitting, setStatusItemUpdateSubmitting] = useState(false);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionTargetFile, setRevisionTargetFile] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [revisionContext, setRevisionContext] = useState(null);
  const [revisionContextLoading, setRevisionContextLoading] = useState(false);

  const pollTimerRef = useRef(null);
  const submittedAtRef = useRef(null);
  const loadSequenceRef = useRef(0);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const parseSubmittedAtMs = useCallback((snapshot = null) => {
    const raw = snapshot?.submittedAt || null;
    const ms = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(ms) ? ms : null;
  }, []);

  const normalizedStatus = String(lifecycle?.normalizedStatus || "").toLowerCase();

  useEffect(() => {
    setSourceResourceId(resourceId || null);
    setLifecycleRequestId(null);
    setLifecycle(null);
    setMode("form");
    submittedAtRef.current = null;
    clearPollTimer();
  }, [clearPollTimer, resourceId]);

  const isTerminalStatus = useCallback(
    (status) => DEFAULT_TERMINAL_STATUSES.has(String(status || "").toLowerCase()),
    []
  );

  const getPollDelayMs = useCallback((submittedAtMs = null) => {
    if (!submittedAtMs) return 30000;
    const elapsedMs = Math.max(0, Date.now() - submittedAtMs);
    return elapsedMs < 3 * 60 * 1000 ? 30000 : 90000;
  }, []);

  const normalizedContextPayload = useMemo(
    () => ({ ...(contextPayload || {}) }),
    [contextPayload]
  );

  const adapterLoadContext = useMemo(
    () => ({
      sourceResourceId,
      contextPayload: normalizedContextPayload,
    }),
    [normalizedContextPayload, sourceResourceId]
  );

  const adapterBaseContext = useMemo(
    () => ({
      ...adapterLoadContext,
      lifecycleRequestId,
    }),
    [adapterLoadContext, lifecycleRequestId]
  );

  const adapterContext = useMemo(
    () => ({
      ...adapterBaseContext,
      lifecycle,
    }),
    [adapterBaseContext, lifecycle]
  );

  const normalizeSnapshot = useCallback(
    (response) => {
      if (typeof adapter?.normalizeResponse === "function") {
        return adapter.normalizeResponse(response, adapterLoadContext);
      }
      return response?.request || response?.lifecycle || response?.workRequest || response || null;
    },
    [adapter, adapterLoadContext]
  );

  const loadLifecycle = useCallback(
    async ({ refresh = false, silent = false } = {}) => {
      if (!enabled || !sourceResourceId || typeof adapter?.load !== "function") return null;
      const loadSequence = loadSequenceRef.current + 1;
      loadSequenceRef.current = loadSequence;
      if (!silent) setLifecycleLoading(true);
      try {
        const response = refresh
          ? await (typeof adapter.refresh === "function"
              ? adapter.refresh(adapterLoadContext)
              : adapter.load(adapterLoadContext))
          : await adapter.load(adapterLoadContext);
        if (loadSequence !== loadSequenceRef.current) return null;
        const nextLifecycle = normalizeSnapshot(response);
        if (nextLifecycle) {
          if (nextLifecycle.requestId || nextLifecycle.id) {
            setLifecycleRequestId(nextLifecycle.requestId || nextLifecycle.id);
          }
          setLifecycle(nextLifecycle);
          setMode("status");
          const submittedAtMs = parseSubmittedAtMs(nextLifecycle);
          if (submittedAtMs) submittedAtRef.current = submittedAtMs;
        }
        return nextLifecycle;
      } catch (err) {
        if (loadSequence !== loadSequenceRef.current) return null;
        if (Number(err?.response?.status || 0) === 404) {
          setLifecycle(null);
          setMode("form");
          return null;
        }
        if (typeof onError === "function") onError(err);
        return null;
      } finally {
        if (!silent && loadSequence === loadSequenceRef.current) setLifecycleLoading(false);
      }
    },
    [adapter, adapterLoadContext, enabled, normalizeSnapshot, onError, parseSubmittedAtMs, sourceResourceId]
  );

  const submitLifecycleRequest = useCallback(
    async (submitFn) => {
      if (typeof submitFn !== "function") throw new Error("submitFn is required");
      setSubmitting(true);
      try {
        await submitFn();
        submittedAtRef.current = Date.now();
        const snap = await loadLifecycle({ refresh: false, silent: true });
        if (!snap) await loadLifecycle({ refresh: true, silent: false });
        setMode("status");
      } finally {
        setSubmitting(false);
      }
    },
    [loadLifecycle]
  );

  const createLifecycleRequest = useCallback(
    async (payload = {}) => {
      if (typeof adapter?.create !== "function") {
        throw new Error("Lifecycle create is not configured for this provider.");
      }

      setSubmitting(true);
      try {
        const response = await adapter.create(adapterContext, payload);
        const nextLifecycle = normalizeSnapshot(response);
        if (nextLifecycle) {
          if (nextLifecycle.requestId || nextLifecycle.id) {
            setLifecycleRequestId(nextLifecycle.requestId || nextLifecycle.id);
          }
          setLifecycle(nextLifecycle);
          setMode("status");
          const submittedAtMs = parseSubmittedAtMs(nextLifecycle) || Date.now();
          submittedAtRef.current = submittedAtMs;
        }
        return nextLifecycle;
      } finally {
        setSubmitting(false);
      }
    },
    [adapter, adapterContext, normalizeSnapshot, parseSubmittedAtMs]
  );

  const cancelLifecycleRequest = useCallback(
    async (payload = {}) => {
      if (typeof adapter?.cancel !== "function") {
        throw new Error("Lifecycle cancel is not configured for this provider.");
      }

      setSubmitting(true);
      try {
        const response = await adapter.cancel(adapterContext, payload);
        const nextLifecycle = normalizeSnapshot(response);
        if (nextLifecycle) {
          if (nextLifecycle.requestId || nextLifecycle.id) {
            setLifecycleRequestId(nextLifecycle.requestId || nextLifecycle.id);
          }
          setLifecycle(nextLifecycle);
          setMode("status");
        }
        return nextLifecycle;
      } finally {
        setSubmitting(false);
      }
    },
    [adapter, adapterContext, normalizeSnapshot]
  );

  const resetLifecycleState = useCallback(() => {
    setMode("form");
    setLifecycle(null);
    setLifecycleRequestId(null);
    submittedAtRef.current = null;
    clearPollTimer();
  }, [clearPollTimer]);

  const openRevisionModalForFile = useCallback(
    async (file = null) => {
      setRevisionTargetFile(file);
      setRevisionNote("");
      setRevisionContext(null);
      setRevisionModalOpen(true);
      if (
        !file?.fileId ||
        !sourceResourceId ||
        typeof adapter?.loadMappingContext !== "function" ||
        typeof buildRevisionContextFromResponse !== "function"
      ) {
        return;
      }

      setRevisionContextLoading(true);
      try {
        const response = await adapter.loadMappingContext(adapterContext, {
          requestId: lifecycle?.requestId || null,
        });
        const nextContext = buildRevisionContextFromResponse(response, file);
        setRevisionContext(nextContext);
        if (nextContext?.defaultNote) {
          setRevisionNote(nextContext.defaultNote);
        }
      } catch (err) {
        if (typeof onError === "function") onError(err);
      } finally {
        setRevisionContextLoading(false);
      }
    },
    [
      adapter,
      buildRevisionContextFromResponse,
      lifecycle?.requestId,
      onError,
      sourceResourceId,
      adapterContext,
    ]
  );

  const closeRevisionModal = useCallback(() => {
    if (revisionSubmitting) return;
    setRevisionModalOpen(false);
    setRevisionContext(null);
    setRevisionContextLoading(false);
  }, [revisionSubmitting]);

  const submitRevision = useCallback(
    async ({ note = "", file = null } = {}) => {
      const trimmed = String(note || "").trim();
      if (!trimmed) throw new Error("Revision note is required.");
      if (!enabled || !sourceResourceId || typeof adapter?.requestRevision !== "function") {
        throw new Error("Missing lifecycle context for revision.");
      }
      setRevisionSubmitting(true);
      try {
        const response = await adapter.requestRevision(adapterContext, {
          note: trimmed,
          fileId: file?.fileId || null,
          mappingId: revisionContext?.mappingId || null,
          proofId: revisionContext?.proofId || null,
          fileRefs: file?.downloadUrl ? [file.downloadUrl] : [],
        });
        const nextLifecycle = normalizeSnapshot(response);
        if (nextLifecycle) {
          setLifecycle(nextLifecycle);
          setMode("status");
        }
        setRevisionModalOpen(false);
        setRevisionTargetFile(null);
        setRevisionNote("");
        setRevisionContext(null);
        return nextLifecycle;
      } finally {
        setRevisionSubmitting(false);
      }
    },
    [adapter, adapterContext, enabled, normalizeSnapshot, revisionContext, sourceResourceId]
  );

  const updateStatusItemDecoration = useCallback(
    async ({ statusItem = null, data = {}, internalNote = "" } = {}) => {
      const requestId = lifecycle?.requestId || null;
      const placementUuid = String(statusItem?.placementUuid || "").trim();
      if (!requestId) {
        throw new Error("Missing lifecycle request id for placement update.");
      }
      if (!placementUuid) {
        throw new Error("Missing placement uuid for placement update.");
      }
      if (typeof adapter?.patchStatusItemDecoration !== "function") {
        throw new Error("Placement update is not configured for this provider.");
      }

      setStatusItemUpdateSubmitting(true);
      try {
        const response = await adapter.patchStatusItemDecoration(adapterContext, {
          requestId,
          placementUuid,
          data,
          internalNote: String(internalNote || "").trim() || null,
        });
        const nextLifecycle = normalizeSnapshot(response);
        if (nextLifecycle) {
          setLifecycle(nextLifecycle);
          setMode("status");
        }
        return nextLifecycle;
      } finally {
        setStatusItemUpdateSubmitting(false);
      }
    },
    [adapter, adapterContext, lifecycle?.requestId, normalizeSnapshot]
  );

  useEffect(() => {
    if (!enabled || !isOpen || !isActive) {
      loadSequenceRef.current += 1;
      setLifecycleLoading(false);
      clearPollTimer();
      return;
    }
    loadLifecycle({ refresh: false, silent: false });
  }, [clearPollTimer, enabled, isActive, isOpen, loadLifecycle]);

  useEffect(() => {
    clearPollTimer();
    if (!enabled || !isOpen || !isActive || mode !== "status") return;
    if (isTerminalStatus(lifecycle?.normalizedStatus)) return;

    const schedulePoll = () => {
      const submittedAtMs = submittedAtRef.current || parseSubmittedAtMs(lifecycle) || Date.now();
      if (!submittedAtRef.current) submittedAtRef.current = submittedAtMs;
      const delayMs = getPollDelayMs(submittedAtMs);
      pollTimerRef.current = setTimeout(async () => {
        const next = await loadLifecycle({ refresh: true, silent: true });
        if (!next || isTerminalStatus(next?.normalizedStatus)) {
          clearPollTimer();
          return;
        }
        schedulePoll();
      }, delayMs);
    };

    schedulePoll();
    return () => clearPollTimer();
  }, [
    clearPollTimer,
    enabled,
    getPollDelayMs,
    isActive,
    isOpen,
    isTerminalStatus,
    lifecycle,
    loadLifecycle,
    mode,
    parseSubmittedAtMs,
  ]);

  useEffect(() => () => clearPollTimer(), [clearPollTimer]);

  const files = useMemo(() => (Array.isArray(lifecycle?.files) ? lifecycle.files : []), [lifecycle]);
  const canShowFiles =
    normalizedStatus === "awaiting_review" ||
    normalizedStatus === "ready" ||
    normalizedStatus === "action_required" ||
    files.length > 0;

  return {
    mode,
    setMode,
    lifecycle,
    normalizedStatus,
    files,
    canShowFiles,
    lifecycleLoading,
    submitting,
    revisionSubmitting,
    statusItemUpdateSubmitting,
    revisionModalOpen,
    revisionTargetFile,
    revisionNote,
    revisionContext,
    revisionContextLoading,
    setRevisionNote,
    isTerminalStatus,
    loadLifecycle,
    refreshLifecycle: () => loadLifecycle({ refresh: true, silent: false }),
    submitLifecycleRequest,
    createLifecycleRequest,
    cancelLifecycleRequest,
    resetLifecycleState,
    openRevisionModalForFile,
    closeRevisionModal,
    submitRevision,
    updateStatusItemDecoration,
  };
}
